import { createApi } from '@reduxjs/toolkit/query/react';
import { adminBaseQuery } from '@store/baseQuery';
import { LookupStatus } from '@/utils/lookupStatus';
import { ConditionLookup } from '@entities/conditions/api';

// Base types


export interface ConditionResourceLookup {
  id: number;
  condition_lookup_id: number;
  condition_lookup?: ConditionLookup; // Optional since it might not always be included
  title: string;
  description: string;
  url: string;
  type: string;
  resource_type: string;
  status: LookupStatus;
  is_valid?: boolean | null;
  created_at?: string;
  updated_at?: string;
}

// Pagination types
export interface PaginationMeta {
  current_page: number;
  first_page_url: string;
  from: number;
  last_page: number;
  last_page_url: string;
  links: Array<{
    url: string | null;
    label: string;
    active: boolean;
  }>;
  next_page_url: string | null;
  path: string;
  per_page: number;
  prev_page_url: string | null;
  to: number;
  total: number;
}

export interface PaginatedResponse<T> {
  current_page: number;
  data: T[];
  first_page_url: string;
  from: number;
  last_page: number;
  last_page_url: string;
  links: Array<{
    url: string | null;
    label: string;
    active: boolean;
  }>;
  next_page_url: string | null;
  path: string;
  per_page: number;
  prev_page_url: string | null;
  to: number;
  total: number;
}

// Query parameter types
export interface ConditionResourcesQueryParams {
  page?: number;
  per_page?: number;
  text?: string;
  speciality_id?: number;
  type?: 'resource' | 'recommendation' | 'prescription';
  resource_type?: string;
  condition_lookup_id?: number;
  sort_by?: 'id' | 'title' | 'type' | 'resource_type' | 'created_at' | 'updated_at';
  sort_direction?: 'asc' | 'desc';
  status?: LookupStatus | 'all';
}

// Request types (updated to match API)
export interface CreateConditionResourceLookupRequest {
  condition_lookup_id: number;
  title?: string; // Now optional
  description?: string; // Now optional
  url: string; // Required
  type: 'resource' | 'recommendation' | 'prescription';
  resource_type: string; // Optional
  status?: LookupStatus; // Optional
  is_valid?: boolean | null; // Optional
}

export interface UpdateConditionResourceLookupRequest {
  condition_lookup_id?: number;
  title?: string;
  description?: string;
  url?: string;
  type?: string;
  resource_type?: string;
  status?: LookupStatus;
  is_valid?: boolean | null;
}

// API with pagination support
export const conditionResourcesLookupsApi = createApi({
  reducerPath: "conditionResourcesLookupsApi",
  baseQuery: adminBaseQuery,
  tagTypes: ["ConditionResourceLookup"],
  endpoints: (builder) => ({
    // Get all condition resources lookups with pagination
    getConditionResourcesLookups: builder.query<PaginatedResponse<ConditionResourceLookup>, ConditionResourcesQueryParams | void>({
      query: (params) => ({
        url: "/condition-resource-lookups",
        params: params || {},
      }),
      providesTags: ["ConditionResourceLookup"],
    }),
    


    // Get a specific condition resources lookup
    getConditionResourceLookup: builder.query<ConditionResourceLookup, number>({
      query: (id) => `/condition-resource-lookups/${id}`,
      providesTags: (result, error, id) => [
        { type: "ConditionResourceLookup", id },
      ],
    }),

    // Create a new condition resources lookup
    createConditionResourceLookup: builder.mutation<
      ConditionResourceLookup,
      CreateConditionResourceLookupRequest
    >({
      query: (data) => ({
        url: "/condition-resource-lookups",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["ConditionResourceLookup"],
    }),

    // Update a specific condition resources lookup
    updateConditionResourceLookup: builder.mutation<
      ConditionResourceLookup,
      { id: number; data: UpdateConditionResourceLookupRequest }
    >({
      query: ({ id, data }) => ({
        url: `/condition-resource-lookups/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "ConditionResourceLookup", id },
        "ConditionResourceLookup",
      ],
    }),

    // Bulk change status
    bulkChangeStatus: builder.mutation<
      { message: string; updated_count: number; failed_count: number; updated_ids: number[] },
      { ids: number[]; status: LookupStatus }
    >({
      query: (data) => ({
        url: "/condition-resource-lookups/bulk-status",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["ConditionResourceLookup"],
    }),

    // Delete a specific condition resources lookup
    deleteConditionResourceLookup: builder.mutation<{ message: string }, number>({
      query: (id) => ({
        url: `/condition-resource-lookups/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "ConditionResourceLookup", id },
        "ConditionResourceLookup",
      ],
    }),
  }),
});

// Export hooks
export const {
  useGetConditionResourcesLookupsQuery,
  useGetConditionResourceLookupQuery,
  useCreateConditionResourceLookupMutation,
  useUpdateConditionResourceLookupMutation,
  useDeleteConditionResourceLookupMutation,
  useBulkChangeStatusMutation,
} = conditionResourcesLookupsApi;

// Re-export with prefixed name to avoid conflicts with conditions API
export { useBulkChangeStatusMutation as useResourceBulkChangeStatusMutation };

// Export reducer
export default conditionResourcesLookupsApi.reducer; 